import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCaisses, useComptesBancaires } from '@/hooks/queries/useBanques';
import {
  useEnregistrerEncaissement,
  type FactureRecouvrementRow,
} from '@/hooks/queries/useRecouvrementEntreprises';
import type { DossierPaiementMode } from '@/hooks/queries/usePaiementsFormateurs';
import { MoneyAmount } from '@/components/design-system';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  facture: FactureRecouvrementRow | null;
}

const MODES: { code: DossierPaiementMode; label: string }[] = [
  { code: 'virement', label: 'Virement' },
  { code: 'cheque', label: 'Chèque' },
  { code: 'especes', label: 'Espèces' },
  { code: 'ccp', label: 'CCP' },
  { code: 'carte', label: 'Carte' },
];

function formatNumberInput(n: number | ''): string {
  if (n === '' || n === 0) return '';
  return n.toLocaleString('fr-DZ', { maximumFractionDigits: 0 });
}

function parseNumberInput(s: string): number {
  const cleaned = s.replace(/[^\d-]/g, '');
  if (!cleaned || cleaned === '-') return 0;
  const n = Number(cleaned);
  return isNaN(n) ? 0 : n;
}

export function EnregistrerEncaissementDialog({ open, onOpenChange, facture }: Props): JSX.Element {
  const enregistrer = useEnregistrerEncaissement();
  const entrepriseId = facture?.entreprise_id ?? null;
  const { data: caisses = [] } = useCaisses(entrepriseId);
  const { data: comptes = [] } = useComptesBancaires(entrepriseId);

  const reste = facture ? facture.montant_ttc - facture.montant_encaisse : 0;

  const [montantEncaisse, setMontantEncaisse] = useState<number | ''>('');
  const [montantDisplay, setMontantDisplay] = useState('');
  const [dateReglement, setDateReglement] = useState(new Date().toISOString().slice(0, 10));
  const [mode, setMode] = useState<DossierPaiementMode>('virement');
  const [reference, setReference] = useState('');
  const [caisseId, setCaisseId] = useState('');
  const [compteId, setCompteId] = useState('');

  useEffect(() => {
    if (!open || !facture) return;
    setMontantEncaisse(reste);
    setMontantDisplay(formatNumberInput(reste));
    setDateReglement(new Date().toISOString().slice(0, 10));
    setMode('virement');
    setReference('');
    setCaisseId('');
    setCompteId('');
  }, [open, facture, reste]);

  useEffect(() => {
    if (mode === 'especes' && caisses.length > 0 && !caisseId) setCaisseId(caisses[0]?.id ?? '');
    if (mode !== 'especes' && comptes.length > 0 && !compteId) setCompteId(comptes[0]?.id ?? '');
  }, [mode, caisses, comptes, caisseId, compteId]);

  if (!facture)
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent />
      </Dialog>
    );

  const isEspeces = mode === 'especes';
  const targetMissing = isEspeces ? !caisseId : !compteId;
  const m = typeof montantEncaisse === 'number' ? montantEncaisse : 0;
  const willBePaid = facture.montant_encaisse + m >= facture.montant_ttc;

  const handleConfirm = async (): Promise<void> => {
    if (m <= 0) {
      toast.error('Montant à encaisser > 0 obligatoire');
      return;
    }
    if (m > reste + 0.01) {
      toast.error(`Montant > reste à encaisser (${reste.toLocaleString('fr-DZ')} DA)`);
      return;
    }
    if (targetMissing) {
      toast.error(isEspeces ? 'Sélectionnez la caisse' : 'Sélectionnez le compte bancaire');
      return;
    }
    try {
      const result = await enregistrer.mutateAsync({
        facture,
        montantEncaisse: m,
        dateReglement,
        modePaiement: mode,
        reference: reference.trim() || null,
        caisseId: isEspeces ? caisseId : null,
        compteBancaireId: isEspeces ? null : compteId,
      });
      toast.success(
        result.nouveau_statut === 'regle'
          ? `Facture soldée · opération ENTRÉE Caisse créée`
          : `Encaissement partiel · opération ENTRÉE créée · reste ${(facture.montant_ttc - facture.montant_encaisse - m).toLocaleString('fr-DZ')} DA`,
      );
      onOpenChange(false);
    } catch (e) {
      toast.error(`Échec : ${(e as Error).message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Encaissement · {facture.entreprise_cliente_nom}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-3 text-sm">
              <div className="text-[10px] uppercase tracking-widest text-blue-700">TTC facturé</div>
              <MoneyAmount amount={facture.montant_ttc} variant="large" />
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3 text-sm">
              <div className="text-[10px] uppercase tracking-widest text-emerald-700">
                Reste à encaisser
              </div>
              <MoneyAmount amount={reste} variant="large" />
              {facture.montant_encaisse > 0 && (
                <div className="text-[10px] text-slate-500">
                  Déjà encaissé : {facture.montant_encaisse.toLocaleString('fr-DZ')} DA
                </div>
              )}
            </div>
          </div>

          <Field label="Montant à encaisser (DA)" required>
            <input
              value={montantDisplay}
              onChange={(e) => {
                const n = parseNumberInput(e.target.value);
                setMontantEncaisse(n === 0 ? '' : n);
                setMontantDisplay(e.target.value.replace(/[^\d ,.-]/g, ''));
              }}
              onBlur={() =>
                montantEncaisse !== '' && setMontantDisplay(formatNumberInput(montantEncaisse))
              }
              className="input tabular-nums"
              inputMode="numeric"
              required
            />
            <span className="mt-0.5 block text-[10px] text-slate-400">
              Max {reste.toLocaleString('fr-DZ')} DA ·{' '}
              {willBePaid ? 'Soldera la facture' : 'Encaissement partiel'}
            </span>
          </Field>

          <Field label="Date de règlement">
            <input
              type="date"
              value={dateReglement}
              onChange={(e) => setDateReglement(e.target.value)}
              className="input"
              required
            />
          </Field>

          <Field label="Mode">
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
              {MODES.map((mm) => (
                <button
                  key={mm.code}
                  type="button"
                  onClick={() => setMode(mm.code)}
                  className={cn(
                    'rounded-lg border-2 px-2 py-2 text-xs font-semibold transition-all',
                    mode === mm.code
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300',
                  )}
                >
                  {mm.label}
                </button>
              ))}
            </div>
          </Field>

          {isEspeces ? (
            <Field
              label="Caisse créditée"
              hint={
                caisses.length === 0 ? '⚠ Aucune caisse — créez-en une dans Paramètres' : undefined
              }
              warn={caisses.length === 0}
            >
              <select
                value={caisseId}
                onChange={(e) => setCaisseId(e.target.value)}
                className="input"
                disabled={caisses.length === 0}
                required
              >
                <option value="">— Sélectionner —</option>
                {caisses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nom}
                    {c.site ? ` · ${c.site}` : ''}
                  </option>
                ))}
              </select>
            </Field>
          ) : (
            <Field
              label="Compte bancaire crédité"
              hint={
                comptes.length === 0 ? '⚠ Aucun compte — créez-en un dans Paramètres' : undefined
              }
              warn={comptes.length === 0}
            >
              <select
                value={compteId}
                onChange={(e) => setCompteId(e.target.value)}
                className="input"
                disabled={comptes.length === 0}
                required
              >
                <option value="">— Sélectionner —</option>
                {comptes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.intitule} {c.numero_compte ? `· ${c.numero_compte}` : ''}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Référence virement (optionnel)" hint="Auto-généré si vide">
            <input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className="input font-mono"
              placeholder="VIR-2026-…"
            />
          </Field>
        </DialogBody>
        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={enregistrer.isPending || targetMissing || m <= 0}
            className={cn(
              'inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-emerald-700',
              'disabled:cursor-not-allowed disabled:opacity-60',
            )}
          >
            {enregistrer.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {willBePaid ? 'Solder la facture' : 'Enregistrer encaissement partiel'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  hint,
  required,
  warn,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  warn?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-slate-700">
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </span>
      {children}
      {hint && (
        <span
          className={cn(
            'mt-0.5 block',
            warn ? 'text-xs font-medium text-amber-600' : 'text-[10px] text-slate-400',
          )}
        >
          {hint}
        </span>
      )}
    </label>
  );
}
